import { useState, useEffect, useRef, memo, type FC } from 'react';
import KawaiiChat from './chat';
import { ChatSidebar } from './sidebar';
import { ChatCreation } from './create';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { KawaiiButton } from '@components/button';
import { twMerge } from 'tailwind-merge';
import { ThemeToggle } from '@components/theme';

const KawaiiChatManager: FC<{ handleSetChatId: (id: number) => void; chatId: number }> = ({
  handleSetChatId,
  chatId,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedState = localStorage.getItem('sidebarCollapsed');
      return savedState === 'true';
    }
    return false;
  });

  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebarCollapsed', String(isCollapsed));
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeof window !== 'undefined' && window.innerWidth < 1600) {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
          setIsCollapsed(true);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleClick = (chatId: number) => {
    handleSetChatId(chatId);
    if (typeof window !== 'undefined' && window.innerWidth < 1600) {
      setIsCollapsed(true);
    }
  };

  return (
    <div className="relative h-full w-full m-auto">
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={twMerge(
          `fixed top-0 left-0 transition-all duration-300 ease-in-out bg-pink-50 overflow-hidden w-48 2xl:w-64
        dark:bg-gray-800 rounded-e-xl shadow-md dark:border-gray-700 border-e-1 border-y-1 shadow-gray-950/60 flex flex-col border-pink-200/50 z-[9999] mt-1`,
        )}
      >
        {/* Controls-only top section */}
        <div className="flex gap-2 items-center px-2 py-1">
          <KawaiiButton
            variant="secondary"
            fullWidth
            onClick={handleToggleCollapse}
            type="button"
            className="p-2 rounded-md bg-transparent shadow-sm shadow-gray-300/50 dark:shadow-gray-900 border-0"
          >
            {isCollapsed ? (
              <ChevronDown strokeWidth={3} className="w-4 h-4 text-pink-600 dark:text-gray-300" />
            ) : (
              <ChevronUp strokeWidth={3} className="w-4 h-4 text-pink-600 dark:text-gray-300" />
            )}
          </KawaiiButton>
          <ChatCreation isCollapsed={isCollapsed} setChatId={handleClick} />
          <ThemeToggle />
        </div>

        {/* Chats only when not collapsed */}
        <div
          className={twMerge(
            'relative flex-1 gap-1 inset-shadow-black transition-all duration-300 scrollbar-thin overflow-x-visible dark:scrollbar-thin flex min-h-0 flex-col overflow-y-auto items-center',
            isCollapsed ? 'max-h-0' : 'max-h-[calc(100vh-40px-16px)]',
          )}
        >
          <div
            className={twMerge(
              'mx-2 z-30 sticky top-0 min-h-[1px] dark:bg-gray-600 w-[95%] transition-all duration-300',
              isCollapsed ? 'opacity-0' : 'opacity-100',
            )}
          />
          <ChatSidebar chatId={chatId} setChatId={handleClick} isCollapsed={isCollapsed} />
        </div>
      </div>

      {/* Main content as usual */}
      <KawaiiChat setChatId={handleSetChatId} chatId={chatId} />
    </div>
  );
};

export default memo(KawaiiChatManager);
